/**
 * Context Update Monitor
 * Monitors context update frequency to detect potential infinite loops
 * Provides performance tracking and early warning system
 */

interface ContextUpdateMetrics {
  contextName: string;
  updateCount: number;
  lastUpdateTime: number;
  averageUpdateInterval: number;
  isWarning: boolean;
  isError: boolean;
}

class ContextUpdateMonitor {
  private metrics = new Map<string, ContextUpdateMetrics>();
  private warningThreshold = 10; // Updates per second
  private errorThreshold = 50; // Updates per second
  private cleanupInterval: NodeJS.Timeout;

  constructor() {
    // Clean up old metrics every 5 seconds
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 5000);
  }

  /**
   * Record a context update
   */
  recordUpdate(contextName: string): void {
    const now = Date.now();
    const existing = this.metrics.get(contextName);

    if (!existing) {
      this.metrics.set(contextName, {
        contextName,
        updateCount: 1,
        lastUpdateTime: now,
        averageUpdateInterval: 0,
        isWarning: false,
        isError: false
      });
      return;
    }

    const timeSinceLastUpdate = now - existing.lastUpdateTime;
    existing.updateCount++;
    existing.lastUpdateTime = now;
    
    // Calculate average update interval
    existing.averageUpdateInterval = 
      existing.averageUpdateInterval === 0 
        ? timeSinceLastUpdate 
        : (existing.averageUpdateInterval + timeSinceLastUpdate) / 2;

    // Check thresholds (updates per second)
    const updatesPerSecond = existing.updateCount / ((now - (now - 1000)) / 1000);
    existing.isWarning = updatesPerSecond > this.warningThreshold;
    existing.isError = updatesPerSecond > this.errorThreshold;

    if (existing.isError) {
      console.error(`🚨 [ContextMonitor] High update frequency in ${contextName}:`, {
        updatesPerSecond: updatesPerSecond.toFixed(2),
        updateCount: existing.updateCount,
        averageInterval: existing.averageUpdateInterval.toFixed(2)
      });
    } else if (existing.isWarning) {
      console.warn(`⚠️ [ContextMonitor] Warning: High update frequency in ${contextName}:`, {
        updatesPerSecond: updatesPerSecond.toFixed(2),
        updateCount: existing.updateCount
      });
    }
  }

  /**
   * Get metrics for a specific context
   */
  getMetrics(contextName: string): ContextUpdateMetrics | undefined {
    return this.metrics.get(contextName);
  }

  /**
   * Get all metrics
   */
  getAllMetrics(): ContextUpdateMetrics[] {
    return Array.from(this.metrics.values());
  }

  /**
   * Check if any context is in error state
   */
  hasErrors(): boolean {
    return Array.from(this.metrics.values()).some(m => m.isError);
  }

  /**
   * Check if any context has warnings
   */
  hasWarnings(): boolean {
    return Array.from(this.metrics.values()).some(m => m.isWarning);
  }

  /**
   * Reset metrics for a specific context
   */
  resetContext(contextName: string): void {
    this.metrics.delete(contextName);
  }

  /**
   * Reset all metrics
   */
  resetAll(): void {
    this.metrics.clear();
  }

  /**
   * Clean up old metrics
   */
  private cleanup(): void {
    const now = Date.now();
    const maxAge = 10000; // 10 seconds

    for (const [contextName, metrics] of this.metrics.entries()) {
      if (now - metrics.lastUpdateTime > maxAge) {
        this.metrics.delete(contextName);
      }
    }
  }

  /**
   * Destroy the monitor
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.metrics.clear();
  }
}

// Global singleton instance
export const contextUpdateMonitor = new ContextUpdateMonitor();

/**
 * Hook for monitoring context updates
 */
import React from 'react';

export const useContextMonitor = (contextName: string) => {
  React.useEffect(() => {
    contextUpdateMonitor.recordUpdate(contextName);
  });

  return {
    metrics: contextUpdateMonitor.getMetrics(contextName),
    hasErrors: contextUpdateMonitor.hasErrors(),
    hasWarnings: contextUpdateMonitor.hasWarnings(),
    resetContext: () => contextUpdateMonitor.resetContext(contextName),
    resetAll: () => contextUpdateMonitor.resetAll()
  };
};

export default ContextUpdateMonitor;
